import React from 'react';

// Formas de pago que se pueden combinar en un pago mixto
export const METODOS_MIXTO = [
    { id: 'efectivo', label: 'Efectivo' },
    { id: 'transferencia', label: 'Transferencia' },
    { id: 'tarjeta', label: 'Tarjeta' },
    { id: 'fiado', label: 'Fiado' },
    { id: 'saldo', label: 'Saldo a favor' }
];

// Formas de pago que siempre van en USD (deuda o saldo del cliente)
const SOLO_USD = ['fiado', 'saldo'];

export const lineasMixtoIniciales = () => [
    { method: 'efectivo', currency: 'USD', amount: '' },
    { method: 'transferencia', currency: 'USD', amount: '' }
];

const toNumber = (value) => parseFloat(String(value ?? '').replace(',', '.')) || 0;

/**
 * Resume las líneas de un pago mixto en montos USD listos para enviar a la orden.
 * - El fiado y el saldo a favor siempre son en USD (deuda / abono del cliente).
 * - Si se entregó de más, el excedente (vuelto) se descuenta del efectivo.
 */
export const resumirPagoMixto = (lineas, total, exchangeRate) => {
    const rate = parseFloat(exchangeRate) || 4000;
    const raw = { CASH_USD: 0, CASH_COP: 0, TRANSFER_USD: 0, TRANSFER_COP: 0, CARD_USD: 0, CARD_COP: 0, CREDIT_USD: 0, BALANCE_USD: 0 };

    lineas.forEach(l => {
        const amount = toNumber(l.amount);
        if (amount <= 0) return;
        if (l.method === 'fiado') {
            raw.CREDIT_USD += amount;
            return;
        }
        if (l.method === 'saldo') {
            raw.BALANCE_USD += amount;
            return;
        }
        const prefix = l.method === 'transferencia' ? 'TRANSFER' : l.method === 'tarjeta' ? 'CARD' : 'CASH';
        raw[`${prefix}_${l.currency === 'COP' ? 'COP' : 'USD'}`] += amount;
    });

    const transfer = raw.TRANSFER_USD + raw.TRANSFER_COP / rate;
    const card = raw.CARD_USD + raw.CARD_COP / rate;
    const credit = raw.CREDIT_USD;
    const balance = raw.BALANCE_USD;
    const entered = raw.CASH_USD + raw.CASH_COP / rate + transfer + card + credit + balance;
    const change = Math.max(0, entered - total);

    // El vuelto sale del efectivo: primero de los dólares y luego de los pesos
    let changeLeft = change;
    const fromUsd = Math.min(changeLeft, raw.CASH_USD);
    raw.CASH_USD -= fromUsd;
    changeLeft -= fromUsd;
    raw.CASH_COP = Math.max(0, raw.CASH_COP - changeLeft * rate);
    const cash = raw.CASH_USD + raw.CASH_COP / rate;

    const note = '[SPLIT] ' + Object.entries(raw).map(([k, v]) => `${k}=${v.toFixed(2)}`).join(' ');

    return {
        entered,
        missing: Math.max(0, total - entered),
        change,
        hasCredit: credit > 0,
        hasBalance: balance > 0,
        cash_amount: Number(cash.toFixed(2)),
        transfer_amount: Number(transfer.toFixed(2)),
        card_amount: Number(card.toFixed(2)),
        credit_amount: Number(credit.toFixed(2)),
        balance_amount: Number(balance.toFixed(2)),
        note,
        // Válido si cubre el total (tolerancia de redondeo) y el fiado / saldo no superan el total
        valid: entered >= total - 0.02 && credit <= total + 0.01 && balance <= total + 0.01 && lineas.some(l => toNumber(l.amount) > 0)
    };
};

export const esPagoMixto = (paymentMethod) => {
    const method = String(paymentMethod || '').toLowerCase();
    return method.includes('dividido') || method.includes('mixto');
};

/**
 * Lee el detalle [SPLIT] guardado en las notas de una orden de pago mixto.
 * Devuelve los montos en USD (los montos en COP se convierten con la tasa indicada).
 */
export const desglosarNotaMixto = (notes, exchangeRate) => {
    const rate = parseFloat(exchangeRate) || 4000;
    const read = (key) => {
        const match = String(notes || '').match(new RegExp(`${key}=([\\d.]+)`));
        return match ? parseFloat(match[1]) || 0 : 0;
    };
    return {
        efectivo_usd: read('CASH_USD'),
        efectivo_cop_in_usd: read('CASH_COP') / rate,
        transferencia_usd: read('TRANSFER_USD'),
        transferencia_cop_in_usd: read('TRANSFER_COP') / rate,
        tarjeta_usd: read('CARD_USD') + read('CARD_COP') / rate,
        fiado_usd: read('CREDIT_USD'),
        saldo_usd: read('BALANCE_USD')
    };
};

// saldoDisponible: saldo a favor del cliente (null = sin cliente). Si no se pasa, la opción "Saldo a favor" no se ofrece.
const PagoMixto = ({ lineas, onChange, total, exchangeRate, hasDebtor, saldoDisponible }) => {
    const permiteSaldo = saldoDisponible !== undefined;
    const rate = parseFloat(exchangeRate) || 4000;
    const resumen = resumirPagoMixto(lineas, total, rate);

    const updateLine = (index, changes) => {
        onChange(lineas.map((l, i) => {
            if (i !== index) return l;
            const next = { ...l, ...changes };
            if (SOLO_USD.includes(next.method)) next.currency = 'USD';
            return next;
        }));
    };

    const completeLine = (index) => {
        const others = resumirPagoMixto(lineas.filter((_, i) => i !== index), total, rate).entered;
        const remainingUsd = Math.max(0, total - others);
        const line = lineas[index];
        updateLine(index, {
            amount: line.currency === 'COP' && !SOLO_USD.includes(line.method)
                ? Math.round(remainingUsd * rate).toString()
                : remainingUsd.toFixed(2)
        });
    };

    const inputStyle = { width: '100%', minWidth: 0, padding: '7px 8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', background: 'white' };

    return (
        <div style={{ padding: '12px', backgroundColor: '#f9fafb', borderRadius: '10px', border: '1px dashed #d1d5db' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', fontSize: '12px' }}>
                <strong style={{ color: 'var(--primary-color)' }}>Pago Mixto</strong>
                <span style={{ color: 'var(--text-secondary)' }}>Tasa COP: {rate}</span>
            </div>

            {lineas.map((line, index) => (
                <div key={index} style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr 1.1fr auto auto', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
                    <select value={line.method} onChange={(e) => updateLine(index, { method: e.target.value })} style={inputStyle}>
                        {METODOS_MIXTO.filter(m => m.id !== 'saldo' || permiteSaldo || line.method === 'saldo').map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                    </select>
                    <select
                        value={line.currency}
                        onChange={(e) => updateLine(index, { currency: e.target.value })}
                        disabled={SOLO_USD.includes(line.method)}
                        style={inputStyle}
                    >
                        <option value="USD">USD</option>
                        <option value="COP">COP</option>
                    </select>
                    <input
                        type="number"
                        step="any"
                        min="0"
                        value={line.amount}
                        onChange={(e) => updateLine(index, { amount: e.target.value })}
                        placeholder="Monto"
                        style={inputStyle}
                    />
                    <button
                        type="button"
                        title="Completar con lo que falta"
                        onClick={() => completeLine(index)}
                        style={{ padding: '6px 8px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontSize: '11px', fontWeight: '600', color: 'var(--primary-color)' }}
                    >
                        Resto
                    </button>
                    <button
                        type="button"
                        title="Quitar línea"
                        onClick={() => onChange(lineas.filter((_, i) => i !== index))}
                        disabled={lineas.length <= 1}
                        style={{ padding: '6px 8px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', cursor: lineas.length <= 1 ? 'not-allowed' : 'pointer', color: '#dc2626' }}
                    >
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>
            ))}

            {lineas.length < 4 && (
                <button
                    type="button"
                    onClick={() => onChange([...lineas, { method: 'efectivo', currency: 'USD', amount: '' }])}
                    style={{ marginTop: '2px', padding: '6px 10px', borderRadius: '6px', border: '1px dashed var(--primary-color)', background: 'white', color: 'var(--primary-color)', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                >
                    <i className="bi bi-plus-lg"></i> Agregar forma de pago
                </button>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: '6px', marginTop: '10px', fontSize: '12px' }}>
                <div style={{ padding: '6px', backgroundColor: '#dcfce7', borderRadius: '6px', color: '#15803d', textAlign: 'center' }}>
                    Cobrado<br /><strong>${(resumen.cash_amount + resumen.transfer_amount + resumen.card_amount).toFixed(2)}</strong>
                </div>
                {resumen.hasCredit && (
                    <div style={{ padding: '6px', backgroundColor: '#fee2e2', borderRadius: '6px', color: '#b91c1c', textAlign: 'center' }}>
                        Fiado<br /><strong>${resumen.credit_amount.toFixed(2)}</strong>
                    </div>
                )}
                {resumen.hasBalance && (
                    <div style={{ padding: '6px', backgroundColor: '#ede9fe', borderRadius: '6px', color: '#6d28d9', textAlign: 'center' }}>
                        Saldo<br /><strong>${resumen.balance_amount.toFixed(2)}</strong>
                    </div>
                )}
                {resumen.missing > 0.01 ? (
                    <div style={{ padding: '6px', backgroundColor: '#fef3c7', borderRadius: '6px', color: '#b45309', textAlign: 'center' }}>
                        Falta<br /><strong>${resumen.missing.toFixed(2)}</strong>
                    </div>
                ) : resumen.change > 0.01 ? (
                    <div style={{ padding: '6px', backgroundColor: '#e0f2fe', borderRadius: '6px', color: '#0369a1', textAlign: 'center' }}>
                        Vuelto<br /><strong>${resumen.change.toFixed(2)}</strong>
                    </div>
                ) : null}
            </div>

            {resumen.hasCredit && !hasDebtor && (
                <div style={{ marginTop: '8px', padding: '8px 10px', backgroundColor: '#fef2f2', borderRadius: '6px', border: '1px solid #fecaca', color: '#991b1b', fontSize: '12px' }}>
                    <i className="bi bi-exclamation-circle-fill"></i> Selecciona el <strong>vendedor</strong> (o un cliente) para saber a quién cobrar la parte fiada.
                </div>
            )}

            {resumen.hasBalance && saldoDisponible === null && (
                <div style={{ marginTop: '8px', padding: '8px 10px', backgroundColor: '#fef2f2', borderRadius: '6px', border: '1px solid #fecaca', color: '#991b1b', fontSize: '12px' }}>
                    <i className="bi bi-exclamation-circle-fill"></i> Selecciona el <strong>cliente</strong> para descontar de su saldo a favor.
                </div>
            )}
            {resumen.hasBalance && saldoDisponible !== null && resumen.balance_amount > saldoDisponible + 0.009 && (
                <div style={{ marginTop: '8px', padding: '8px 10px', backgroundColor: '#fef2f2', borderRadius: '6px', border: '1px solid #fecaca', color: '#991b1b', fontSize: '12px' }}>
                    <i className="bi bi-exclamation-circle-fill"></i> El cliente solo tiene <strong>${saldoDisponible.toFixed(2)}</strong> de saldo a favor.
                </div>
            )}
        </div>
    );
};

export default PagoMixto;
